/* eb-launch.js v2 - behaviour for the EasyBlock launch section (no dependencies). Works for every .eb-launch on the page and
   re-initialises when the theme editor reloads the section.
   - scroll-scrubbed frame sequences on a sticky canvas (landscape frames on wide screens, portrait renders on phones);
     frame 000 loads at once, the rest only when the sequence comes within ~1.5 screens, a few requests at a time
   - captions/words switched by scroll progress (data-from / data-to, 0..1; 1 = until the end)
   - the product bar and the sequences sit under the theme's sticky header (measured while scrolling) when enabled
   - videos pick their landscape/portrait source, load near the viewport and play only while in view; the film opens
     in an accessible modal (focus moved and kept inside, page scroll locked, Esc closes)
   - reveal-on-scroll; prefers-reduced-motion: posters instead of autoplay, a still frame instead of scrubbing */
(() => {
  const portrait = () => window.matchMedia("(max-aspect-ratio: 4/5)").matches;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const orient = () => (portrait() ? "p" : "l");
  const pad = (n) => String(n).padStart(3, "0");
  const MAX_INFLIGHT = 6;

  function setVideoSource(v) {
    const o = orient(), src = (o === "p" && v.dataset.srcP) || v.dataset.srcL || v.dataset.srcP;
    const poster = (o === "p" && v.dataset.posterP) || v.dataset.posterL;
    if (poster && v.getAttribute("poster") !== poster) v.poster = poster;
    if (reduced && v.hasAttribute("data-eb-autoplay")) return;   // reduced motion: the poster stays
    if (!src || v.dataset.current === src) return;
    v.dataset.current = src; v.src = src; v.load();
    if (v.hasAttribute("data-eb-autoplay") && !reduced) v.play().catch(() => {});
  }

  // the theme's own sticky/fixed header: how far down the viewport it currently reaches (0 when hidden or not sticky)
  function themeHeaderBottom(root) {
    let bottom = 0;
    document.querySelectorAll(".shopify-section-header, .shopify-section-group-header-group, sticky-header, header, [data-sticky-header]").forEach((el) => {
      if (root.contains(el)) return;
      const pos = getComputedStyle(el).position; if (pos !== "sticky" && pos !== "fixed") return;
      const r = el.getBoundingClientRect(); if (r.top <= 1 && r.bottom > 0 && r.height < window.innerHeight / 3) bottom = Math.max(bottom, r.bottom);
    });
    return Math.round(bottom);
  }

  class Sequence {
    constructor(el, root) {
      this.el = el; this.root = root; this.count = Math.max(1, +el.dataset.count || 1);
      this.canvas = el.querySelector(".eb-seq__canvas"); this.ctx = this.canvas.getContext("2d");
      this.sticky = el.querySelector(".eb-seq__sticky") || el;
      this.labels = [...el.querySelectorAll("[data-from]")];
      this.frames = []; this.o = null; this.current = -1; this.started = false; this.load();
    }
    base(o) {   // the first frame's URL (Files CDN, with ?v=) -> everything before "000.webp"
      const first = (o === "p" && this.el.dataset.firstP) || this.el.dataset.firstL || this.el.dataset.firstP || "";
      const clean = first.split("?")[0];
      return clean ? { pre: clean.replace(/000\.webp$/, ""), q: first.includes("?") ? "?" + first.split("?")[1] : "" } : null;
    }
    get(i) {
      const img = new Image(); img.decoding = "async"; img.src = `${this.b.pre}${pad(i)}.webp${this.b.q}`; this.frames[i] = img; return img;
    }
    load() {   // (re)start for the current orientation: frame 000 now, the rest once start() is called
      const o = orient(); if (o === this.o) return; this.o = o; this.frames = new Array(this.count); this.current = -1; this.gen = (this.gen || 0) + 1;
      this.b = this.base(o); if (!this.b) return;
      if (reduced) { this.get(this.still()).onload = () => this.draw(true); return; }   // reduced motion: one still, the one its caption matches
      this.get(0).onload = () => this.draw(true);
      if (this.started) { this.started = false; this.start(); }
    }
    start() {   // coarse to fine (every 16th frame first), at most MAX_INFLIGHT requests at once
      if (this.started || !this.b || reduced) return; this.started = true;
      const gen = this.gen, order = [], seen = new Set([0]);
      for (const step of [16, 8, 4, 2, 1]) for (let i = 0; i < this.count; i += step) if (!seen.has(i)) { seen.add(i); order.push(i); }
      let k = 0, inflight = 0;
      const pump = () => {
        while (gen === this.gen && inflight < MAX_INFLIGHT && k < order.length) {
          const i = order[k++], img = this.get(i); inflight++;
          img.onload = img.onerror = () => { inflight--; if (Math.abs(i - this.current) < 3 || this.current < 0) this.draw(); pump(); };
        }
      };
      pump();
    }
    still() { return Math.round(0.95 * (this.count - 1)); }
    stickyTop() { return parseFloat(getComputedStyle(this.sticky).top) || 0; }
    progress() {
      const r = this.el.getBoundingClientRect(), top = this.stickyTop(), span = r.height - this.sticky.offsetHeight;
      return Math.min(1, Math.max(0, (top - r.top) / Math.max(1, span)));
    }
    nearest(i) {
      for (let d = 0; d < this.count; d++) for (const j of [i - d, i + d]) { const f = this.frames[j]; if (f && f.complete && f.naturalWidth) return f; }
      return null;
    }
    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2), w = this.canvas.clientWidth, h = this.canvas.clientHeight;
      if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
        this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr); this.current = -1;
      }
    }
    draw(force = false) {
      this.resize();
      const p = reduced ? 0.95 : this.progress(), i = Math.round(p * (this.count - 1));
      this.caption(p);
      if (!force && i === this.current) return;
      const img = reduced ? this.frames[this.still()] : this.nearest(i); if (!img || !img.naturalWidth) return;
      this.current = img === this.frames[i] ? i : -1;
      const cw = this.canvas.width, ch = this.canvas.height, s = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * s, h = img.naturalHeight * s;
      this.ctx.fillStyle = "#000"; this.ctx.fillRect(0, 0, cw, ch);
      this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }
    caption(p) {
      for (const l of this.labels) { const to = +l.dataset.to; l.classList.toggle("eb-is-on", p >= +l.dataset.from && (p < to || to >= 1)); }
    }
  }

  class Spin extends Sequence {   // the beauty shot: turns as the block passes through the screen; the still image shows until then
    progress() {
      const r = this.el.getBoundingClientRect(), vh = window.innerHeight;
      return Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
    }
    draw(force = false) { super.draw(force); if (this.current >= 0 || this.frames[0]?.naturalWidth) this.el.classList.add("eb-spin-ready"); }
  }

  function initModal(root) {
    const modal = root.querySelector("[data-eb-modal]"), openers = root.querySelectorAll("[data-eb-film-open]");
    if (!modal) { openers.forEach((b) => (b.hidden = true)); return; }
    const film = modal.querySelector("video"), closeBtn = modal.querySelector("[data-eb-film-close]");
    let opener = null;
    const close = () => {
      if (modal.hidden) return;
      film.pause(); film.removeAttribute("src"); film.load(); modal.hidden = true;
      document.documentElement.style.overflow = ""; if (opener) opener.focus();
    };
    openers.forEach((b) => b.addEventListener("click", () => {
      opener = b; film.src = (portrait() && film.dataset.srcP) || film.dataset.srcL; modal.hidden = false;
      document.documentElement.style.overflow = "hidden"; closeBtn.focus(); film.play().catch(() => {});
    }));
    closeBtn.addEventListener("click", close);
    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
    modal.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key !== "Tab") return;   // keep focus on the two controls inside the dialog
      const items = [closeBtn, film], i = items.indexOf(document.activeElement);
      e.preventDefault(); items[(i + (e.shiftKey ? items.length - 1 : 1)) % items.length].focus();
    });
  }

  function init(root) {
    if (root.dataset.ebReady) return; root.dataset.ebReady = "1"; root.classList.add("eb-js");
    if (reduced) root.classList.add("eb-reduced");

    const videos = [...root.querySelectorAll("[data-eb-video]")];
    const hero = videos.filter((v) => !v.hasAttribute("data-eb-inview")); hero.forEach(setVideoSource);
    const lazy = videos.filter((v) => v.hasAttribute("data-eb-inview"));
    lazy.forEach((v) => { const p = (orient() === "p" && v.dataset.posterP) || v.dataset.posterL; if (p) v.poster = p; });
    const near = new IntersectionObserver((es) => { for (const e of es) if (e.isIntersecting) { e.target.dataset.near = "1"; setVideoSource(e.target); near.unobserve(e.target); } }, { rootMargin: "100% 0px" });
    const inview = new IntersectionObserver((es) => { for (const e of es) { if (reduced) continue; if (e.isIntersecting) e.target.play().catch(() => {}); else e.target.pause(); } }, { threshold: 0.35 });
    lazy.forEach((v) => { near.observe(v); inview.observe(v); });

    const seqs = [...root.querySelectorAll("[data-eb-seq]")].map((el) => new Sequence(el, root))
      .concat([...root.querySelectorAll("[data-eb-spin]")].map((el) => new Spin(el, root)));
    const soon = new IntersectionObserver((es) => { for (const e of es) if (e.isIntersecting) { const s = seqs.find((q) => q.el === e.target); if (s) s.start(); soon.unobserve(e.target); } }, { rootMargin: "150% 0px" });
    seqs.forEach((s) => soon.observe(s.el));

    const auto = (root.dataset.autoSticky || "").toLowerCase() === "true";
    let lastTop = -1, maxTop = 0;   // top follows a hiding header; the sequences' height uses its full height (no resize jumps)
    const sticky = () => {
      if (!auto) return; const t = themeHeaderBottom(root);
      if (t > maxTop) { maxTop = t; root.style.setProperty("--eb-sticky-max", t + "px"); }
      if (t !== lastTop) { lastTop = t; root.style.setProperty("--eb-sticky-top", t + "px"); }
    };
    sticky();

    let ticking = false;
    const frame = () => { sticky(); seqs.forEach((s) => s.draw()); ticking = false; };
    const onScroll = () => {
      if (document.hidden) { frame(); return; }   // hidden tabs get no animation frames
      if (ticking) return; ticking = true; requestAnimationFrame(frame);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", () => {
      sticky(); seqs.forEach((s) => { s.load(); s.draw(true); });
      hero.forEach(setVideoSource); lazy.filter((v) => v.dataset.near).forEach(setVideoSource);
    });

    const rev = new IntersectionObserver((es) => { for (const e of es) if (e.isIntersecting) { e.target.classList.add("eb-is-on"); rev.unobserve(e.target); } }, { threshold: 0.15 });
    root.querySelectorAll(".eb-reveal").forEach((el) => rev.observe(el));

    initModal(root);
  }

  const boot = () => document.querySelectorAll("[data-eb-launch]").forEach(init);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
  document.addEventListener("shopify:section:load", boot);   // theme editor re-renders the section
})();
